/**
 * AdminBreadcrumb — the branded orientation trail in the header's eyebrow slot.
 *
 * Path-derived (not Refine-derived) so it renders identically on EVERY route:
 * CRUD pages, curated-nav pages, and customs like /analytics/screens or
 * /send-push. The default @refinedev/antd breadcrumb inside Show/Edit/Create
 * cards is disabled globally (App.tsx `options.breadcrumb: false`) in favor of
 * this single, consistent placement.
 *
 * Design decisions (kept deliberately quiet — it lives above a large display
 * title and must not compete with it):
 *  - typographic trail in the established eyebrow voice (Montserrat, uppercase,
 *    wide tracking) rather than boxed/pill crumbs;
 *  - crest-gold marks the CURRENT page (same accent as the old static eyebrow),
 *    muted slate for ancestors, gold on hover;
 *  - chevron separators (never slashes) at low contrast;
 *  - a crumb links ONLY when it's a real destination: a curated ADMIN_NAV path,
 *    or the resource-list segment immediately before a show/edit/create action
 *    (e.g. /event_sessions in /event_sessions/edit/5). Raw record ids are
 *    dropped from the trail — "View"/"Edit" already says where you are and the
 *    page title identifies the record;
 *  - <nav aria-label="Breadcrumb"> + aria-current="page" for assistive tech.
 */
import React from 'react';
import { Link, useLocation } from 'react-router';
import { brand } from '../theme/brand';
import { ADMIN_NAV, activeNav, type AdminNavGroup } from './adminNav';
import { Home, ChevronRight } from './icons';

/** Route → the label a person clicks to reach it: sidebar items AND their tabs. */
function navLabels(nav: AdminNavGroup[]): Record<string, string> {
  return Object.fromEntries(
    nav.flatMap((g) =>
      g.items.flatMap((it) => [[it.to, it.label], ...(it.tabs ?? []).map((t) => [t.to, t.label])]),
    ),
  );
}

const ACTION_LABELS: Record<string, string> = {
  show: 'View',
  edit: 'Edit',
  create: 'New',
  clone: 'Clone',
};

/** Record ids (numeric or uuid-ish) carry no wayfinding value — drop them. */
const isIdSegment = (seg: string): boolean =>
  /^\d+$/.test(seg) || /^[0-9a-fA-F]{8}[0-9a-fA-F-]*$/.test(seg);

const prettify = (seg: string): string =>
  seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export interface Crumb {
  label: string;
  /** Present only when the crumb is a navigable destination. */
  href?: string;
}

const segmentsOf = (path: string): string[] =>
  path.split('/').filter(Boolean).filter((s) => !isIdSegment(s));

/** Path-derived trail: one crumb per URL segment, from `base` onward. */
function segmentCrumbs(pathname: string, labels: Record<string, string>, base = ''): Crumb[] {
  const segments = segmentsOf(pathname.slice(base.length));
  const crumbs: Crumb[] = [];
  let acc = base;
  segments.forEach((seg, i) => {
    acc += `/${seg}`;
    const next = segments[i + 1];
    const label = labels[acc] ?? ACTION_LABELS[seg] ?? prettify(decodeURIComponent(seg));
    const isLast = i === segments.length - 1;
    const navigable = !isLast && (acc in labels || (next !== undefined && next in ACTION_LABELS));
    crumbs.push(navigable ? { label, href: acc } : { label });
  });
  return crumbs;
}

/**
 * Pure trail builder — exported for tests. On a route that is one tab of a
 * tabbed sidebar entry the trail reads `Section › Tab` (the section linking to
 * its landing tab), then any action crumbs — e.g. Groups › Meetups › View. The
 * section crumb is skipped when the tab carries the section's own name.
 */
export function buildCrumbs(pathname: string, nav: AdminNavGroup[] = ADMIN_NAV): Crumb[] {
  const labels = navLabels(nav);
  const active = activeNav(pathname, nav);
  if (!active?.tab || (active.item.tabs?.length ?? 0) < 2) return segmentCrumbs(pathname, labels);

  const rest = segmentCrumbs(pathname, labels, active.to);
  const crumbs: Crumb[] = [];
  if (active.tab.label !== active.item.label) crumbs.push({ label: active.item.label, href: active.item.to });
  crumbs.push(rest.length > 0 ? { label: active.tab.label, href: active.to } : { label: active.tab.label });
  return [...crumbs, ...rest];
}

const eyebrowFont: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
};

export function AdminBreadcrumb() {
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);
  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb">
      {/* inline styles can't express :hover; one tiny scoped rule keeps the
          gold hover without introducing a css file to an inline-styled app */}
      <style>{`
        .muaa-crumb-link { color: ${brand.sub}; text-decoration: none; transition: color .15s ease; }
        .muaa-crumb-link:hover, .muaa-crumb-link:focus-visible { color: ${brand.gold}; }
      `}</style>
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          listStyle: 'none',
          margin: 0,
          padding: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <li style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" aria-label="Dashboard" className="muaa-crumb-link" style={{ display: 'flex' }}>
            <Home size={12} strokeWidth={2.2} />
          </Link>
        </li>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={`${c.label}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <ChevronRight size={11} color="#b9c0cf" strokeWidth={2.4} />
              {c.href ? (
                <Link to={c.href} className="muaa-crumb-link" style={eyebrowFont}>
                  {c.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  style={{
                    ...eyebrowFont,
                    fontWeight: isLast ? 700 : 600,
                    color: isLast ? brand.gold : brand.sub,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 260,
                  }}
                >
                  {c.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
